import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

import { useExclusiveDrawer } from "../../drawerCoordinator";
import type { WorkflowChatContext } from "../workflowChat";

/**
 * ui2026: the docked Neo side panel ("Ask Circadia AI"). Lives in the app
 * shell so it stays open across page changes; the page beside it keeps
 * scrolling and simply gets narrower.
 */

interface NeoDrawerSession {
  /** Remounts the conversation when the user starts or switches chats. */
  sessionKey: string;
  conversationId: number | null;
  /** Sent once, as soon as the conversation is ready. */
  initialPrompt: string | null;
  /** Set while a conversation is being created elsewhere (e.g. from a flag):
   *  the panel shows this label with a loading state. */
  pendingLabel?: string | null;
  /** Chat about a workflow: the panel shows its latest output and sends it
   *  as context with the first message. */
  workflow?: WorkflowChatContext | null;
}

interface NeoDrawerContextValue {
  open: boolean;
  session: NeoDrawerSession;
  /** Open on a fresh conversation, optionally sending a first message. */
  openNewChat: (prompt?: string) => void;
  /** Open a new chat about a workflow's latest output. */
  openWorkflowChat: (workflow: WorkflowChatContext) => void;
  /** Open (or switch to) an existing conversation. */
  openConversation: (conversationId: number, prompt?: string) => void;
  close: () => void;
  /** Open right away in a loading state while the caller creates the
   *  conversation. Returns a token for resolvePending / cancelPending. */
  openPending: (label: string) => string;
  /** Switch the pending panel to the created conversation — only if the user
   *  hasn't moved on to another chat in the meantime. */
  resolvePending: (
    token: string,
    conversationId: number,
    prompt?: string
  ) => void;
  /** Creation failed: close the panel if it's still waiting on it. */
  cancelPending: (token: string) => void;
  /** A new chat got its backend id; keep it without remounting. */
  adoptConversation: (conversationId: number) => void;
  consumeInitialPrompt: () => void;
}

const newSession = (prompt?: string): NeoDrawerSession => ({
  sessionKey: `drawer-new-${Date.now()}`,
  conversationId: null,
  initialPrompt: prompt?.trim() || null,
});

const NeoDrawerContext = createContext<NeoDrawerContextValue | null>(null);

export const NeoDrawerProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<NeoDrawerSession>(() => newSession());
  const sessionKeyRef = useRef(session.sessionKey);
  sessionKeyRef.current = session.sessionKey;

  const openNewChat = useCallback((prompt?: string) => {
    setSession(newSession(prompt));
    setOpen(true);
  }, []);

  const openWorkflowChat = useCallback((workflow: WorkflowChatContext) => {
    const key = `drawer-workflow-${Date.now()}`;
    sessionKeyRef.current = key;
    setSession({
      sessionKey: key,
      conversationId: null,
      initialPrompt: null,
      workflow,
    });
    setOpen(true);
  }, []);

  const openConversation = useCallback(
    (conversationId: number, prompt?: string) => {
      setSession((current) =>
        current.conversationId === conversationId && !prompt
          ? current
          : {
              sessionKey: `drawer-conversation-${conversationId}-${Date.now()}`,
              conversationId,
              initialPrompt: prompt?.trim() || null,
            }
      );
      setOpen(true);
    },
    []
  );

  const close = useCallback(() => setOpen(false), []);
  useExclusiveDrawer(open, close);

  const openPending = useCallback((label: string) => {
    const token = `drawer-pending-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;
    // Synchronously, so a request that fails before the next render can
    // still cancel this panel.
    sessionKeyRef.current = token;
    setSession({
      sessionKey: token,
      conversationId: null,
      initialPrompt: null,
      pendingLabel: label,
    });
    setOpen(true);
    return token;
  }, []);

  const resolvePending = useCallback(
    (token: string, conversationId: number, prompt?: string) => {
      setSession((current) =>
        current.sessionKey === token
          ? {
              sessionKey: `drawer-conversation-${conversationId}-${Date.now()}`,
              conversationId,
              initialPrompt: prompt?.trim() || null,
            }
          : current
      );
    },
    []
  );

  const cancelPending = useCallback((token: string) => {
    // Only when the panel is still waiting on this request.
    if (sessionKeyRef.current !== token) return;
    setOpen(false);
    setSession(newSession());
  }, []);

  const adoptConversation = useCallback((conversationId: number) => {
    setSession((current) => ({ ...current, conversationId }));
  }, []);

  const consumeInitialPrompt = useCallback(() => {
    setSession((current) =>
      current.initialPrompt ? { ...current, initialPrompt: null } : current
    );
  }, []);

  const value = useMemo(
    () => ({
      open,
      session,
      openNewChat,
      openWorkflowChat,
      openConversation,
      close,
      openPending,
      resolvePending,
      cancelPending,
      adoptConversation,
      consumeInitialPrompt,
    }),
    [
      open,
      session,
      openNewChat,
      openWorkflowChat,
      openConversation,
      close,
      openPending,
      resolvePending,
      cancelPending,
      adoptConversation,
      consumeInitialPrompt,
    ]
  );

  return (
    <NeoDrawerContext.Provider value={value}>
      {children}
    </NeoDrawerContext.Provider>
  );
};

/** Null outside the 2026 shell (legacy layout, tests), so callers can fall
 *  back to the full Copilot page. */
export const useNeoDrawer = (): NeoDrawerContextValue | null =>
  useContext(NeoDrawerContext);
